import { useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { Phone, MessageCircle, CheckCircle, Globe } from "lucide-react";

const ARTWORK_URL = "https://customer-assets.emergentagent.com/job_grand-jeu-sale/artifacts/9zkklcuv_La_Reine_Demente_encadree.jpg";

const content = {
  en: {
    artistName: "Josef Šíma",
    artworkTitle: "La Reine démente",
    year: "1960",
    technique: "Gouache and charcoal on paper",
    dimensions: "32 × 23.5 cm",
    provenance: "Private Collection",
    price: "Price on Request",
    bio: `A seminal figure of the Grand Jeu movement, Josef Šíma (1891–1971) bridged the gap between French Surrealism and Czech avant-garde. His works are held in the Centre Pompidou and major private collections. In 2020, his auction record reached`,
    auctionRecord: "€173,542",
    certificateText: "Certificate of Authenticity Included",
    photoDisclaimer: "* The photograph does not faithfully reflect the impeccable quality of the work nor the excellence of its conservation. The original presents details and a depth of material far superior to what the reproduction conveys. A private viewing is strongly recommended.",
    callButton: "Call",
    whatsappButton: "Send a Message",
    seller: "Seller",
    specsTitle: "Technical Specifications",
    aboutArtist: "About the Artist",
    contact: "Contact",
    privateSale: "Private Sale",
  },
  fr: {
    artistName: "Josef Šíma",
    artworkTitle: "La Reine démente",
    year: "1960",
    technique: "Gouache et fusain sur papier",
    dimensions: "32 × 23,5 cm",
    provenance: "Collection Privée",
    price: "Prix sur Demande",
    bio: `Figure séminale du mouvement Le Grand Jeu, Josef Šíma (1891–1971) a fait le lien entre le surréalisme français et l'avant-garde tchèque. Ses œuvres sont conservées au Centre Pompidou et dans d'importantes collections privées. En 2020, son record aux enchères a atteint`,
    auctionRecord: "173 542 €",
    certificateText: "Certificat d'Authenticité Inclus",
    photoDisclaimer: "* La photographie ne reflète pas fidèlement la qualité impeccable du tableau ni l'excellence de sa conservation. L'œuvre originale présente des détails et une profondeur de matière bien supérieurs à ce que la reproduction permet d'apprécier. Une consultation en privé est vivement recommandée.",
    callButton: "Appeler",
    whatsappButton: "Envoyer un message",
    seller: "Vendeur",
    specsTitle: "Spécifications Techniques",
    aboutArtist: "À propos de l'Artiste",
    contact: "Contact",
    privateSale: "Vente Privée",
  },
};

const hashtags = [
  "#JosefSima", "#LeGrandJeu", "#ArtTchèque", "#CzechArt", "#Artcurial",
  "#Drouot", "#GalerieModernaPrague", "#CentrePompidou", "#ArtCollector",
  "#VentePrivée", "#PrivateSale", "#FineArt", "#ArtInvestment",
  "#Gouache", "#Fusain", "#1960", "#Paris", "#Prague"
];

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function App() {
  const [lang, setLang] = useState("en");
  const t = content[lang];

  return (
    <div className="min-h-screen bg-void text-paper relative" data-testid="art-gallery-app">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Header with Language Toggle */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6">
        <div className="max-w-[1400px] mx-auto flex justify-end">
          <div className="flex items-center gap-3" data-testid="language-toggle">
            <Globe className="w-4 h-4 text-gold/60" />
            <button
              onClick={() => setLang("en")}
              className={`lang-toggle-btn font-sans text-xs uppercase tracking-[0.2em] ${lang === "en" ? "active" : ""}`}
              data-testid="lang-en-btn"
            >
              EN
            </button>
            <span className="text-gold/30">/</span>
            <button
              onClick={() => setLang("fr")}
              className={`lang-toggle-btn font-sans text-xs uppercase tracking-[0.2em] ${lang === "fr" ? "active" : ""}`}
              data-testid="lang-fr-btn"
            >
              FR
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="min-h-screen pt-24 md:pt-0 px-6 md:px-12 lg:px-24" data-testid="hero-section">
          <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 md:gap-12 items-center min-h-screen py-12">
            {/* Artwork Image - Left Side */}
            <motion.div 
              className="md:col-span-5 lg:col-span-6 relative"
              initial={{ scale: 1.05, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <div className="artwork-glow absolute inset-0 -m-12" />
              <div className="relative">
                <img
                  src={ARTWORK_URL}
                  alt={`${t.artworkTitle} by ${t.artistName}`}
                  className="w-full h-auto object-contain artwork-shadow"
                  data-testid="artwork-image"
                />
                <p 
                  className="mt-4 font-sans text-xs text-paper/50 leading-relaxed italic"
                  data-testid="photo-disclaimer"
                >
                  {t.photoDisclaimer}
                </p>
              </div>
            </motion.div>

            {/* Text Content - Right Side */}
            <motion.div 
              className="md:col-span-7 lg:col-span-6"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              <motion.p 
                variants={fadeInUp}
                className="font-sans uppercase tracking-[0.2em] text-xs text-gold/80 mb-4"
                data-testid="private-sale-label"
              >
                {t.privateSale}
              </motion.p>
              
              <motion.h1 
                variants={fadeInUp}
                className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-none mb-4"
                data-testid="artist-name"
              >
                {t.artistName}
              </motion.h1>
              
              <motion.h2 
                variants={fadeInUp}
                className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-gold mb-4"
                data-testid="artwork-title"
              >
                {t.artworkTitle}
              </motion.h2>
              
              <motion.p 
                variants={fadeInUp}
                className="font-sans text-sm text-paper/60 tracking-wide mb-8"
                data-testid="artwork-year-technique"
              >
                {t.year} — {t.technique}
              </motion.p>

              {/* Certificate Badge */}
              <motion.div 
                variants={fadeInUp}
                className="flex items-center gap-3 mb-8"
                data-testid="certificate-badge"
              >
                <CheckCircle className="w-5 h-5 text-gold" />
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-gold/90">
                  {t.certificateText}
                </span>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap gap-4"
              >
                <a
                  href="tel:+33673883769"
                  className="btn-primary uppercase tracking-[0.15em] text-xs px-8 py-4 font-medium flex items-center gap-3"
                  data-testid="call-button"
                >
                  <Phone className="w-4 h-4" />
                  {t.callButton}
                </a>
                <a
                  href="https://wa.me/15144515435"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline uppercase tracking-[0.15em] text-xs px-8 py-4 font-medium flex items-center gap-3"
                  data-testid="whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.whatsappButton}
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0a0a08]" data-testid="specs-section">
          <div className="max-w-[1400px] mx-auto">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-sans uppercase tracking-[0.2em] text-xs text-gold/80 mb-12"
              data-testid="specs-title"
            >
              {t.specsTitle}
            </motion.h3>
            
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-2xl"
              data-testid="specs-table"
            >
              <div className="specs-row py-4 grid grid-cols-2 gap-4">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50">Technique</span>
                <span className="font-sans text-sm text-paper/90">{t.technique}</span>
              </div>
              <div className="specs-row py-4 grid grid-cols-2 gap-4">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50">Dimensions</span>
                <span className="font-sans text-sm text-paper/90">{t.dimensions}</span>
              </div>
              <div className="specs-row py-4 grid grid-cols-2 gap-4">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50">{lang === "en" ? "Year" : "Année"}</span>
                <span className="font-sans text-sm text-paper/90">{t.year}</span>
              </div>
              <div className="specs-row py-4 grid grid-cols-2 gap-4">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50">Provenance</span>
                <span className="font-sans text-sm text-paper/90">{t.provenance}</span>
              </div>
              <div className="specs-row py-4 grid grid-cols-2 gap-4">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50">{lang === "en" ? "Price" : "Prix"}</span>
                <span className="font-sans text-sm text-gold font-medium">{t.price}</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Artist Bio */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="bio-section">
          <div className="max-w-[1400px] mx-auto">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-sans uppercase tracking-[0.2em] text-xs text-gold/80 mb-12"
              data-testid="bio-title"
            >
              {t.aboutArtist}
            </motion.h3>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-xl sm:text-2xl lg:text-3xl leading-relaxed text-paper/80 max-w-4xl"
              data-testid="bio-text"
            >
              {t.bio} <span className="text-gold font-medium">{t.auctionRecord}</span>.
            </motion.p>
          </div>
        </section>

        {/* Hashtags */}
        <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-t border-gold/10" data-testid="hashtags-section">
          <div className="max-w-[1400px] mx-auto">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-wrap gap-3 md:gap-4"
              data-testid="hashtags-container"
            >
              {hashtags.map((tag, index) => (
                <span
                  key={index}
                  className="hashtag font-sans text-xs tracking-wide border border-gold/30 px-3 py-1.5 cursor-default"
                  data-testid={`hashtag-${index}`}
                >
                  {tag}
                </span>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Contact Footer */}
        <footer className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-[#0a0a08] border-t border-gold/10" data-testid="contact-footer">
          <div className="max-w-[1400px] mx-auto text-center">
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-sans uppercase tracking-[0.2em] text-xs text-gold/80 mb-8"
              data-testid="contact-title"
            >
              {t.contact}
            </motion.h3>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-8"
            >
              <p className="font-sans text-xs uppercase tracking-[0.15em] text-paper/50 mb-2">{t.seller}</p>
              <p className="font-serif text-2xl text-paper" data-testid="seller-name">Ibarola</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <p className="font-sans text-sm text-paper/60 mb-1" data-testid="phone-number">
                <a href="tel:+33673883769" className="hover:text-gold transition-colors">
                  +33 6 73 88 37 69
                </a>
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <a
                href="tel:+33673883769"
                className="btn-primary uppercase tracking-[0.15em] text-xs px-8 py-4 font-medium flex items-center gap-3"
                data-testid="footer-call-button"
              >
                <Phone className="w-4 h-4" />
                {t.callButton}
              </a>
              <a
                href="https://wa.me/15144515435"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline uppercase tracking-[0.15em] text-xs px-8 py-4 font-medium flex items-center gap-3"
                data-testid="footer-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4" />
                {t.whatsappButton}
              </a>
            </motion.div>

            {/* Copyright */}
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 font-sans text-xs text-paper/30"
              data-testid="copyright"
            >
              © {new Date().getFullYear()} Ibarola. {lang === "en" ? "All rights reserved." : "Tous droits réservés."}
            </motion.p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;
